import type { FeatureSettings } from '../types';

const CLASS_MAP: Record<
    keyof Omit<FeatureSettings, 'fontSize' | 'skipToContent'>,
    string
> = {
    textSpacing: 'inclusive-text-spacing',
    highContrast: 'inclusive-high-contrast',
    grayscale: 'inclusive-grayscale',
    invertColors: 'inclusive-invert',
    highlightLinks: 'inclusive-highlight-links',
    largeCursor: 'inclusive-large-cursor',
    reduceMotion: 'inclusive-reduce-motion',
    keyboardNavigation: 'inclusive-keyboard-navigation',
    focusIndicators: 'inclusive-focus-indicators',
    readingMode: 'inclusive-reading-mode',
};

export function applySettings(settings: FeatureSettings): void {
    const root = document.documentElement;
    const scale = 1 + settings.fontSize * 0.125;

    root.style.setProperty(
        '--inclusive-font-scale',
        String(Math.max(0.875, scale)),
    );
    root.classList.toggle('inclusive-font-scale', settings.fontSize !== 0);

    (Object.keys(CLASS_MAP) as Array<keyof typeof CLASS_MAP>).forEach((key) => {
        root.classList.toggle(CLASS_MAP[key], Boolean(settings[key]));
    });

    ensureHostStyles();
    ensureSkipLink(settings.skipToContent);
}

export function resetDom(): void {
    applySettings({
        fontSize: 0,
        textSpacing: false,
        highContrast: false,
        grayscale: false,
        invertColors: false,
        highlightLinks: false,
        largeCursor: false,
        reduceMotion: false,
        keyboardNavigation: false,
        focusIndicators: false,
        readingMode: false,
        skipToContent: false,
    });
}

function ensureSkipLink(enabled: boolean): void {
    const existing = document.getElementById('inclusive-skip-link');

    if (!enabled) {
        existing?.remove();

        return;
    }

    if (
        existing ||
        document.querySelector('[href="#content"], [href="#main"]')
    ) {
        return;
    }

    const link = document.createElement('a');
    link.id = 'inclusive-skip-link';
    link.href = '#main';
    link.textContent = 'Skip to content';
    link.className = 'inclusive-skip-link';
    document.body.prepend(link);

    if (!document.getElementById('main') && !document.querySelector('main')) {
        const main = document.querySelector('h1')?.parentElement;
        main?.setAttribute('id', 'main');
    }
}

function ensureHostStyles(): void {
    let style = document.getElementById('inclusive-host-styles');

    if (!(style instanceof HTMLStyleElement)) {
        style = document.createElement('style');
        style.id = 'inclusive-host-styles';
        document.head.appendChild(style);
    }

    style.textContent = hostStyles;
}

const hostStyles = `
html.inclusive-font-scale { font-size: calc(100% * var(--inclusive-font-scale, 1)) !important; }
html.inclusive-high-contrast { background: #111111 !important; }
html.inclusive-high-contrast body { background: #111111 !important; color: #ffffff !important; }
html.inclusive-high-contrast body *:not(#inclusive-widget) { color: #ffffff !important; border-color: #a8c030 !important; background-color: #111111 !important; }
html.inclusive-high-contrast img, html.inclusive-high-contrast svg, html.inclusive-high-contrast video, html.inclusive-high-contrast canvas { background-color: transparent !important; }
html.inclusive-grayscale { filter: grayscale(1) !important; }
html.inclusive-invert { filter: invert(1) hue-rotate(180deg) !important; }
html.inclusive-highlight-links a { outline: 2px solid #a8c030 !important; text-decoration: underline !important; }
html.inclusive-large-cursor, html.inclusive-large-cursor * { cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Cpath fill='%23000' stroke='%23fff' stroke-width='2' d='M4 4 L4 40 L16 30 L28 46 L34 42 L22 26 L36 26 Z'/%3E%3C/svg%3E") 4 4, auto !important; }
html.inclusive-reduce-motion *, html.inclusive-reduce-motion *::before, html.inclusive-reduce-motion *::after { animation: none !important; transition: none !important; scroll-behavior: auto !important; }
html.inclusive-focus-indicators *:focus { outline: 3px solid #a8c030 !important; outline-offset: 2px !important; }
html.inclusive-text-spacing body, html.inclusive-text-spacing body *:not(#inclusive-widget) { letter-spacing: 0.08em !important; word-spacing: 0.16em !important; line-height: 1.8 !important; }
html.inclusive-reading-mode img, html.inclusive-reading-mode video, html.inclusive-reading-mode iframe, html.inclusive-reading-mode aside, html.inclusive-reading-mode nav { opacity: 0.35; }
html.inclusive-keyboard-navigation :focus { outline: 3px solid #a8c030; }
.inclusive-skip-link { position: absolute; left: -999px; top: 0; background: #a8c030; color: #111111; padding: 0.75rem 1rem; z-index: 2147483646; }
.inclusive-skip-link:focus { left: 1rem; top: 1rem; }
@media (prefers-reduced-motion: reduce) {
  html.inclusive-reduce-motion *, html.inclusive-reduce-motion *::before, html.inclusive-reduce-motion *::after { animation: none !important; transition: none !important; }
}
`;

